import React, { useMemo } from 'react';
import { Subscribe } from 'unstated';
import { css } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import Box from '@atlaskit/primitives/box';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import { xcss } from '@atlaskit/primitives/xcss';
import type { SelectionExtensionSelectionInfo } from '@atlaskit/editor-plugin-selection-extension/types';

import type { Extension } from '@atlassian/forge-ui/web-client/graphql/types';

import { FORGE_MODULE_CONTEXT_MENU } from '@confluence/forge-ui/entry-points/ForgeModuleType.ts';
import { ContextMenuForgeApp } from '@confluence/forge-ui/entry-points/apps.ts';
import { getContentType } from '@confluence/forge-ui/entry-points/contentType.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { usePageState } from '@confluence/page-context';
import { useSpaceIdentifiers } from '@confluence/space-utils/entry-points/useSpaceIdentifiers.ts';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { CoverPictureStateContainer } from '@confluence/page-cover-picture/entry-points/CoverPictureStateContainer.ts';

// Rough estimate for page padding avg height (comes from byline/title) - doesn't need to be exact since the popup will float in editor
// I'm optimizing for single byline as most instances have an average of 2 - 3 apps in the byline and shouldn't be multiline
const PAGE_PADDING_TOP = 135;
// Rough estimate for cover picture avg height - doesn't need to be exact since the popup will float in editor
const COVER_PICTURE_PADDING_TOP = 275;

const popupWrapperStyle = xcss({
	padding: 'space.250',
	maxWidth: '500px',
	maxHeight: '500px',
});

const triggerStyles = css({
	position: 'absolute',
	pointerEvents: 'none',
});

type ForgeEditorActionsProps = {
	contentId?: string;
	forgeExtension: Extension;
	onClose: () => void;
	selection: SelectionExtensionSelectionInfo;
};

export const ForgeEditorAction = ({
	contentId,
	forgeExtension,
	onClose,
	selection,
}: ForgeEditorActionsProps): React.JSX.Element => {
	const isLivePage = useIsCurrentPageLive();
	const [{ routeName }] = usePageState();
	const { spaceId, spaceKey } = useSpaceIdentifiers();
	const { Popup } = useChoreographerAssets();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleInitialRender = () => {
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				source: 'editorActionsToolbarMenu',
				action: 'shown',
				actionSubject: 'forgeApp',
				actionSubjectId: 'editorActionsMenuForgeApp',
				attributes: {
					contentId,
					isLivePage,
					appInfo: {
						id: forgeExtension?.id,
						appId: forgeExtension?.appId,
						key: forgeExtension?.key,
						appVersion: forgeExtension?.appVersion,
						appOwner: forgeExtension?.appOwner,
					},
				},
			},
		}).fire();
	};

	// Bridge needs to be memoised here.
	// Otherwise the useMemo hook in NativeUIRenderer will trigger an infinite rerender loop on ContextMenuForgeApp when using the Frame component.
	const memoedBridge = useMemo(() => ({ close: onClose }), [onClose]);

	return (
		<Subscribe to={[CoverPictureStateContainer]}>
			{(coverPictureState: CoverPictureStateContainer) => (
				<Popup
					content={() => (
						<Box xcss={popupWrapperStyle} testId="forge-editor-popup-content">
							<ErrorBoundary attribution={Attribution.PAGE_EXPERIENCES}>
								<ContextMenuForgeApp
									app={forgeExtension}
									extensionData={{
										type: FORGE_MODULE_CONTEXT_MENU,
										selectedText: selection.text,
										content: {
											id: contentId || '',
											type: getContentType(routeName),
											subtype: isLivePage ? 'live' : null,
										},
										space: {
											key: spaceKey,
											id: spaceId,
										},
									}}
									onInitialRender={handleInitialRender}
									onTearDown={onClose}
									bridge={memoedBridge}
								/>
							</ErrorBoundary>
						</Box>
					)}
					trigger={({ ref }) => {
						// Adjust left position based on if side nav is open or not - it will push the content too left if we don't check
						// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
						const sideNavWidth = document.getElementById('side-navigation')?.offsetWidth || 0;
						const leftCoords = selection.coords.left - sideNavWidth;

						// Adjust top position based on if cover picture is present or not - it will be too high otherwise
						const topCoords = coverPictureState.state.hasCoverPicture
							? selection.coords.top + COVER_PICTURE_PADDING_TOP - PAGE_PADDING_TOP
							: selection.coords.top - PAGE_PADDING_TOP;
						return (
							<span
								data-testid="forge-editor-action-selection"
								ref={ref}
								css={triggerStyles}
								style={{
									// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Dynamic positioning based on selection coordinates
									top: topCoords,
									// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Dynamic positioning based on selection coordinates
									left: leftCoords,
									width: selection.coords.right - selection.coords.left,
									height: selection.coords.bottom - selection.coords.top,
								}}
							/>
						);
					}}
					isOpen
					placement="bottom"
				/>
			)}
		</Subscribe>
	);
};
